import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Building2, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", cnpj: "", contact_email: "", city: "", is_active: true };

const FIELD_KEYS = [
  ["name", "label_name", true],
  ["cnpj", "label_cnpj", false],
  ["contact_email", "tenants_contact_email", false],
  ["city", "city", false],
];

function TenantFormFields({ form, setForm, t, mode }) {
  const isEdit = mode === "edit";

  return (
    <>
      {FIELD_KEYS.map(([k, labelKey, required]) => (
        <div key={k}>
          <label className="text-xs font-medium text-[var(--text-2)]">{t(labelKey)}</label>
          <Input
            value={form[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            required={required}
            className="rounded-xl h-10 mt-1"
            data-testid={`tenant-input-${k}`}
          />
          {k === "city" && (
            <p className="text-[10px] text-[var(--text-3)] mt-1">{t("tenants_city_hint")}</p>
          )}
        </div>
      ))}
      {isEdit && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={form.is_active}
            onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
            data-testid="tenant-input-active"
          />
          {t("users_active")}
        </label>
      )}
    </>
  );
}

function TenantFormDialog({ open, onOpenChange, mode, tenant, t, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    if (isEdit && tenant) {
      setForm({
        name: tenant.name || "",
        cnpj: tenant.cnpj || "",
        contact_email: tenant.contact_email || "",
        city: tenant.city || "",
        is_active: tenant.is_active !== false,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, isEdit, tenant]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        cnpj: form.cnpj.trim() || null,
        contact_email: form.contact_email.trim() || null,
        city: form.city.trim() || null,
      };

      if (isEdit) {
        payload.is_active = form.is_active;
        await api.patch(`/tenants/${tenant.id}`, payload);
        toast.success(t("tenants_updated"));
      } else {
        await api.post("/tenants", payload);
        toast.success(t("tenants_created"));
      }
      onSaved();
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          (isEdit ? t("tenants_update_failed") : t("tenants_create_failed")),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={isEdit ? "tenant-edit-dialog" : "tenant-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display font-bold">
            {isEdit ? t("tenants_edit") : t("tenants_new")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <TenantFormFields form={form} setForm={setForm} t={t} mode={mode} />
          <Button
            type="submit"
            disabled={saving}
            className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11"
            data-testid="tenant-submit"
          >
            {saving ? t("saving") : isEdit ? t("users_save") : t("tenants_create_btn")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Tenants() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);

  const load = useCallback(async () => {
    const [{ data }, { data: s }] = await Promise.all([api.get("/tenants"), api.get("/tenants/stats")]);
    setItems(data);
    setStats(s);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">Multi-tenant</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_tenants")}</h1>
          <p className="text-xs text-[var(--text-2)] mt-1">{t("tenants_subtitle")}</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4"
          data-testid="new-tenant-btn"
        >
          <Plus size={14} className="mr-1" /> {t("tenants_new")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((tn) => (
          <div key={tn.id} className="card-soft p-5" data-testid={`tenant-${tn.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
                <Building2 size={22} />
              </div>
              <div className="flex items-center gap-2">
                <span className={`pill ${tn.is_active ? "pill-success" : "pill-neutral"}`}>
                  {tn.is_active ? t("users_active") : t("users_inactive")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-xl"
                  onClick={() => setEditTenant(tn)}
                  data-testid={`tenant-edit-${tn.id}`}
                  aria-label={t("tenants_edit")}
                >
                  <Pencil size={16} />
                </Button>
              </div>
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight">{tn.name}</h3>
            <div className="text-xs text-[var(--text-2)] mt-3 space-y-1">
              {tn.cnpj && (
                <div>
                  {t("label_cnpj")} <span className="font-mono">{tn.cnpj}</span>
                </div>
              )}
              {tn.city && <div>{tn.city}</div>}
              {tn.contact_email && <div className="truncate">{tn.contact_email}</div>}
            </div>
            <div className="border-t border-[var(--border-c)] mt-4 pt-3 flex items-center justify-between">
              <div className="text-xs text-[var(--text-2)] font-medium">{t("report_documents")}</div>
              <div className="font-display text-2xl font-bold">{stats[tn.id] || 0}</div>
            </div>
            <div className="text-[10px] text-[var(--text-3)] font-mono mt-2">{fmtDate(tn.created_at)}</div>
          </div>
        ))}
      </div>

      <TenantFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        tenant={null}
        t={t}
        onSaved={() => {
          setCreateOpen(false);
          load();
        }}
      />
      <TenantFormDialog
        open={!!editTenant}
        onOpenChange={(open) => {
          if (!open) setEditTenant(null);
        }}
        mode="edit"
        tenant={editTenant}
        t={t}
        onSaved={() => {
          setEditTenant(null);
          load();
        }}
      />
    </div>
  );
}
